import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { forgotPassword } from "../../services/authApi";
import { useAuth } from "../../context/AuthContext";

const TABS = {
  LOGIN: "login",
  REGISTER: "register",
  FORGOT: "forgot",
};

const AuthModal = ({
  open,
  isOpen,
  onClose,
  titleMessage = "",
  initialMessage = "",
  /** Shown after successful login (overrides generic default). */
  loginSuccessMessage,
  /** Shown after successful registration (overrides generic default). */
  registerSuccessMessage,
}) => {
  const { login, register, refreshFromStorage } = useAuth();
  const [tab, setTab] = useState(TABS.LOGIN);
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [forgotEmail, setForgotEmail] = useState("");

  const heading = useMemo(() => {
    if (tab === TABS.REGISTER) return "Create Treydora Account";
    if (tab === TABS.FORGOT) return "Forgot Password";
    return "Treydora Login";
  }, [tab]);

  const modalOpen = typeof isOpen === "boolean" ? isOpen : Boolean(open);
  const introMessage = initialMessage || titleMessage;

  if (!modalOpen) return null;

  const resetMessages = () => {
    setErrorMessage("");
    setStatusMessage("");
  };

  const onLoginSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    try {
      await login(loginForm.email.trim(), loginForm.password);
      refreshFromStorage();
      setStatusMessage(loginSuccessMessage || "You're signed in.");
    } catch (error) {
      setErrorMessage(error?.message || "Unable to log in right now.");
    } finally {
      setBusy(false);
    }
  };

  const onRegisterSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    if (registerForm.password !== registerForm.confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    const displayName = registerForm.displayName.trim();
    const fallbackUsername = registerForm.email.split("@")[0] || "treydora-user";
    setBusy(true);
    try {
      await register({
        username: displayName || fallbackUsername,
        email: registerForm.email.trim(),
        password: registerForm.password,
      });
      refreshFromStorage();
      setStatusMessage(
        registerSuccessMessage || "Account created. You're signed in."
      );
      setTab(TABS.LOGIN);
    } catch (error) {
      setErrorMessage(error?.message || "Unable to create account right now.");
    } finally {
      setBusy(false);
    }
  };

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    try {
      const response = await forgotPassword(forgotEmail.trim());
      setStatusMessage(
        response?.message ||
          "If your email exists, a reset code has been sent."
      );
    } catch (error) {
      setErrorMessage(error?.message || "Unable to send reset code right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="treydora-auth-modal-root" role="dialog" aria-modal="true" aria-labelledby="treydora-auth-title">
      <button type="button" className="treydora-auth-modal-backdrop" onClick={onClose} aria-label="Close auth modal" />
      <div className="treydora-auth-modal">
        <button type="button" className="treydora-auth-modal__close" onClick={onClose} aria-label="Close dialog">
          ×
        </button>
        <h3 id="treydora-auth-title" className="treydora-auth-modal__title">{heading}</h3>
        {introMessage && <p className="treydora-auth-modal__subtitle">{introMessage}</p>}

        <div className="treydora-auth-modal__tabs" role="tablist" aria-label="Auth tabs">
          <button type="button" className={`treydora-auth-tab ${tab === TABS.LOGIN ? "is-active" : ""}`} onClick={() => { setTab(TABS.LOGIN); resetMessages(); }}>
            Login
          </button>
          <button type="button" className={`treydora-auth-tab ${tab === TABS.REGISTER ? "is-active" : ""}`} onClick={() => { setTab(TABS.REGISTER); resetMessages(); }}>
            Create Account
          </button>
          <button type="button" className={`treydora-auth-tab ${tab === TABS.FORGOT ? "is-active" : ""}`} onClick={() => { setTab(TABS.FORGOT); resetMessages(); }}>
            Forgot Password
          </button>
        </div>

        {tab === TABS.LOGIN && (
          <form className="treydora-auth-modal__form" onSubmit={onLoginSubmit}>
            <input type="email" placeholder="Email" value={loginForm.email} onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))} required />
            <input type="password" placeholder="Password" value={loginForm.password} onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))} required />
            <button type="submit" className="oshar-btn-gold" disabled={busy}>{busy ? "Logging In..." : "Log In"}</button>
          </form>
        )}

        {tab === TABS.REGISTER && (
          <form className="treydora-auth-modal__form" onSubmit={onRegisterSubmit}>
            <input type="text" placeholder="Display Name" value={registerForm.displayName} onChange={(e) => setRegisterForm((prev) => ({ ...prev, displayName: e.target.value }))} />
            <input type="email" placeholder="Email" value={registerForm.email} onChange={(e) => setRegisterForm((prev) => ({ ...prev, email: e.target.value }))} required />
            <input type="password" placeholder="Password" value={registerForm.password} onChange={(e) => setRegisterForm((prev) => ({ ...prev, password: e.target.value }))} required />
            <input type="password" placeholder="Confirm Password" value={registerForm.confirmPassword} onChange={(e) => setRegisterForm((prev) => ({ ...prev, confirmPassword: e.target.value }))} required />
            <button type="submit" className="oshar-btn-gold" disabled={busy}>{busy ? "Creating..." : "Create Account"}</button>
          </form>
        )}

        {tab === TABS.FORGOT && (
          <form className="treydora-auth-modal__form" onSubmit={onForgotSubmit}>
            <input type="email" placeholder="Email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
            <button type="submit" className="oshar-btn-gold" disabled={busy}>{busy ? "Sending..." : "Send Reset Code"}</button>
          </form>
        )}

        {errorMessage && <p className="treydora-auth-modal__error">{errorMessage}</p>}
        {statusMessage && <p className="treydora-auth-modal__success">{statusMessage}</p>}

        <div className="td-auth-modal__footer-links">
          Full-page sign-in: <Link to="/login">Login</Link>
          <Link to="/signup">Sign up</Link>
          <Link to="/forgot-password">Forgot password</Link>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
